import {
    Box,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    InputAdornment,
    Link,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import PathList from "../../Common/PathList";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import Loader from "../../Components/Loader";

// Roster of everyone who has EVER had a premium transaction. All data
// arrives in one call (~70 rows); search/sort/CSV happen client-side.

const PLAN_LABELS = {
    "com.treatwalk.dog.app.monthly": "Monthly",
    "com.treatwalk.dog.app.annually": "Annual",
    "com.treatwalk.dog.app.monthly.referral": "Monthly (referral)",
    "com.treatwalk.dog.app.annually.referral": "Annual (referral)",
};

const STATUS_STYLES = {
    active: { label: "Active", bg: "#e8f5e9", fg: "#1b5e20" },
    trial: { label: "In trial", bg: "#e3f2fd", fg: "#0d47a1" },
    churned: { label: "Churned", bg: "#fbe9e7", fg: "#a33c1d" },
};

// Fiverr-era dev/test accounts: throwaway domains + known sandbox emails.
const TEST_PATTERNS = [/@yopmail\./i, /@testwalk\./i, /@treatwalk\.com/i, /@gmmm\./i, /@tresdtf\./i,
    /^max@gmail\.com/i, /^harsh@gmail\.com/i, /^vivekp08031@/i, /^lakhwanideepak01@/i];
const isTestAccount = (email) => TEST_PATTERNS.some((p) => p.test(email || ""));

const dayDiff = (a, b) => Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

// Row shaping: derive the display/sort fields once, up front.
const shapeRow = (u) => {
    const status = !u.has_active ? "churned" : u.in_trial ? "trial" : "active";
    const [rc_type, rc_date] = (u.latest_rc_event || "").split("|");
    return {
        ...u,
        status,
        rc_event: rc_type ? `${rc_type} ${fmtDate(rc_date)}` : null,
        is_test: isTestAccount(u.email),
        name: [u.fname, u.lname].filter(Boolean).join(" ") || u.username || "—",
        plan: PLAN_LABELS[u.latest_plan_id] || u.latest_plan_id || "—",
        premium_days: dayDiff(u.premium_since, new Date()),
        days_to_premium: dayDiff(u.signed_up_at, u.premium_since),
        // last_app_open_at is a new column — dormant users fall back to
        // their latest activity date.
        last_use: u.last_app_open_at || u.last_activity_at || null,
        goals_text: (() => {
            try { const g = typeof u.goals === "string" ? JSON.parse(u.goals) : u.goals; return Array.isArray(g) ? g.join(", ") : ""; } catch { return ""; }
        })(),
    };
};

const COLUMNS = [
    { key: "name", label: "User" },
    { key: "email", label: "Email" },
    { key: "dogs", label: "Dogs" },
    { key: "signed_up_at", label: "Signed up", numeric: true },
    { key: "plan", label: "Plan" },
    { key: "status", label: "Status" },
    { key: "premium_since", label: "Premium since", numeric: true },
    { key: "premium_days", label: "Premium for", numeric: true },
    { key: "days_to_premium", label: "Signup→Premium", numeric: true },
    { key: "total_activities", label: "Activities", numeric: true },
    { key: "last_use", label: "Last use", numeric: true },
    { key: "streak_count", label: "Streak", numeric: true },
    { key: "has_pmf", label: "PMF", numeric: true },
    { key: "rc_event", label: "Last RC event" },
    { key: "referred_by", label: "Referred by" },
    { key: "heard_from", label: "Heard from" },
    { key: "goals_text", label: "Goals" },
];

const PremiumUsers = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(""); // "" | active | trial | churned
    const [hideTest, setHideTest] = useState(true);
    const [orderBy, setOrderBy] = useState("premium_since");
    const [order, setOrder] = useState("desc");

    useEffect(() => {
        (async () => {
            try {
                const response = await AxiosInstance.get(ApiList.PREMIUM_USERS);
                if (response?.data?.s === 1) setRows((response.data.r || []).map(shapeRow));
                else setError(response?.data?.m || "Failed to load premium users");
            } catch (e) {
                console.error("Error fetching premium users:", e);
                setError("Could not reach the server");
            }
            setLoading(false);
        })();
    }, []);

    const handleSort = (key) => {
        if (orderBy === key) setOrder(order === "asc" ? "desc" : "asc");
        else { setOrderBy(key); setOrder("desc"); }
    };

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        let out = rows.filter((r) =>
            (!hideTest || !r.is_test) &&
            (!statusFilter || r.status === statusFilter) &&
            (!term || [r.name, r.email, r.username, r.dogs].some((v) => v && String(v).toLowerCase().includes(term)))
        );
        const dir = order === "asc" ? 1 : -1;
        out.sort((a, b) => {
            const av = a[orderBy], bv = b[orderBy];
            if (av == null && bv == null) return 0;
            if (av == null) return 1; // nulls last regardless of direction
            if (bv == null) return -1;
            if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
            // Date-ish columns sort chronologically.
            const ad = Date.parse(av), bd = Date.parse(bv);
            if (!isNaN(ad) && !isNaN(bd)) return (ad - bd) * dir;
            return String(av).localeCompare(String(bv)) * dir;
        });
        return out;
    }, [rows, search, statusFilter, hideTest, order, orderBy]);

    const exportCsv = () => {
        const head = COLUMNS.map((c) => c.label);
        const lines = visible.map((r) =>
            [r.name, r.email, r.dogs, fmtDate(r.signed_up_at), r.plan, STATUS_STYLES[r.status].label,
             fmtDate(r.premium_since), `${r.premium_days} days`, `${r.days_to_premium} days`,
             r.total_activities, fmtDate(r.last_use), r.streak_count, r.has_pmf ? "Yes" : "No",
             r.rc_event || "No RC data", r.referred_by || "", r.heard_from || "", r.goals_text || ""]
                .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
        const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `premium-users-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const countBy = (s) => rows.filter((r) => r.status === s).length;

    return (
        <Box sx={{ width: "100%", height: "100vh", background: "#B9CC0310", overflow: "hidden" }}>
            <Box sx={{ width: "100%", p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: { xs: "18px", md: "28px" }, fontWeight: "300", color: "#000" }}>
                    Premium Users
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                        size="small"
                        placeholder="Search name, email, or dog"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ background: "#fff", borderRadius: "8px", minWidth: "260px" }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: "#00000060" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Chip
                        icon={<DownloadIcon sx={{ fontSize: 18 }} />}
                        label="CSV"
                        onClick={exportCsv}
                        sx={{ cursor: "pointer", background: "#fff", border: "1px solid #00000020", "&:hover": { background: "#f2f6d9" } }}
                    />
                </Box>
            </Box>

            <Box sx={{ width: "100%", height: "calc(100vh - 74px)", p: 2, pt: 0, overflow: "auto" }}>
                {/* Status filter chips with live counts */}
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                    {[
                        { key: "", label: `All (${rows.length})` },
                        { key: "active", label: `Active (${countBy("active")})` },
                        { key: "trial", label: `In trial (${countBy("trial")})` },
                        { key: "churned", label: `Churned (${countBy("churned")})` },
                    ].map((f) => (
                        <Chip
                            key={f.key || "all"}
                            label={f.label}
                            onClick={() => setStatusFilter(f.key)}
                            sx={{
                                cursor: "pointer",
                                fontWeight: statusFilter === f.key ? 600 : 400,
                                background: statusFilter === f.key ? "#c6ff00" : "#fff",
                                border: "1px solid #00000020",
                                "&:hover": { background: statusFilter === f.key ? "#c6ff00" : "#f2f6d9" },
                            }}
                        />
                    ))}
                    <Box sx={{ flex: 1 }} />
                    <Chip
                        label={hideTest ? "Test accounts hidden" : "Test accounts shown"}
                        onClick={() => setHideTest(!hideTest)}
                        sx={{
                            cursor: "pointer",
                            background: hideTest ? "#fff" : "#fff3cd",
                            border: "1px solid #00000020",
                            "&:hover": { background: "#f2f6d9" },
                        }}
                    />
                </Box>

                {loading ? (
                    <Box sx={{ width: "100%", height: "400px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Loader />
                    </Box>
                ) : error ? (
                    <Box sx={{ width: "100%", height: "300px", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Typography sx={{ color: "#00000080", fontSize: "16px" }}>{error}</Typography>
                    </Box>
                ) : (
                    <TableContainer sx={{ background: "#fff", borderRadius: "10px", border: "1px solid #00000012", mb: 2 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {COLUMNS.map((c) => (
                                        <TableCell key={c.key} sx={{ whiteSpace: "nowrap", fontWeight: 600, background: "#fafbef" }}>
                                            <TableSortLabel
                                                active={orderBy === c.key}
                                                direction={orderBy === c.key ? order : "desc"}
                                                onClick={() => handleSort(c.key)}
                                            >
                                                {c.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visible.map((r) => {
                                    const st = STATUS_STYLES[r.status];
                                    return (
                                        <TableRow key={r.user_id} hover>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                                                    {r.name}
                                                    {r.is_test && (
                                                        <Chip label="Test" size="small" sx={{ ml: 0.5, height: 18, fontSize: "11px", background: "#fff3cd", color: "#7a5c00" }} />
                                                    )}
                                                </Typography>
                                                {r.username && (
                                                    <Typography sx={{ fontSize: "12px", color: "#00000070" }}>@{r.username}</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{r.email || "—"}</TableCell>
                                            <TableCell sx={{ maxWidth: 160 }}>{r.dogs || "—"}</TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(r.signed_up_at)}</TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{r.plan}</TableCell>
                                            <TableCell>
                                                <Chip label={st.label} size="small" sx={{ background: st.bg, color: st.fg, fontWeight: 600 }} />
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(r.premium_since)}</TableCell>
                                            <TableCell align="right">{r.premium_days} d</TableCell>
                                            <TableCell align="right">{r.days_to_premium} d</TableCell>
                                            <TableCell align="right">{r.total_activities}</TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(r.last_use)}</TableCell>
                                            <TableCell align="right">{r.streak_count}</TableCell>
                                            <TableCell>
                                                {r.has_pmf ? (
                                                    // Jump to the Feedbacks dashboard filtered by their email.
                                                    <Link
                                                        component="button"
                                                        onClick={() => navigate(PathList.FeedbacksModule, { state: { search: r.email } })}
                                                        sx={{ fontSize: "13px", fontWeight: 600 }}
                                                    >
                                                        Yes →
                                                    </Link>
                                                ) : ("—")}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                {r.rc_event || (
                                                    // No webhook history: sandbox purchase or pre-webhook user —
                                                    // cross-check against the RevenueCat dashboard.
                                                    <Chip label="No RC data" size="small" sx={{ background: "#eceff1", color: "#455a64" }} />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{r.referred_by || "—"}</TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{r.heard_from || "—"}</TableCell>
                                            <TableCell sx={{ maxWidth: 200 }}>{r.goals_text || "—"}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Box>
    );
};

export default PremiumUsers;
